import type {
  SideMenuCategoriesByRole,
  SideMenuCategory,
  SideMenuFeatureItem,
} from '@indutrust/shared-model';

const REPORTING_MENU_ITEM = {
  icon: 'chart-bar',
  id: 'reporting',
  route: ['/reporting'],
  title: 'itemList.reporting.label',
} satisfies SideMenuFeatureItem;

const RESOURCES_CATEGORY = {
  id: 'resources',
  icon: '',
  label: 'categories.resources',
  items: [
    {
      id: 'documentation',
      title: 'itemList.documentation.label',
      icon: 'file',
      route: ['/docs'],
    },
    {
      id: 'helpCenter',
      title: 'itemList.helpCenter.label',
      icon: 'question-circle',
      route: ['/help-center'],
    },
  ],
} satisfies SideMenuCategory;

/**
 * Indutrust platform side menu categories, keyed by user role.
 */
export const INDUTRUST_SIDE_MENU_BY_ROLE: SideMenuCategoriesByRole = {
  admin: [
    {
      id: 'features',
      icon: '',
      label: 'categories.features',
      items: [
        {
          id: 'dashboard',
          title: 'itemList.dashboard.label',
          icon: 'gauge',
          route: ['/dashboard'],
        },
        {
          id: 'map',
          title: 'itemList.map.label',
          icon: 'map',
          route: ['/map'],
        },
        {
          id: 'projects',
          title: 'itemList.projects.label',
          icon: 'briefcase',
          route: ['/projects'],
        },
        REPORTING_MENU_ITEM,
      ],
    },
    {
      collapsible: true,
      defaultExpanded: false,
      id: 'organization',
      icon: '',
      label: 'categories.organization',
      items: [
        {
          id: 'company',
          title: 'itemList.company.label',
          icon: 'building',
          route: ['/company'],
        },
        {
          id: 'team',
          title: 'itemList.team.label',
          icon: 'users',
          route: ['/company', ':companyId', 'team'],
        },
        {
          id: 'factories',
          title: 'itemList.factories.label',
          icon: 'warehouse',
          route: ['/factories'],
        },
        {
          id: 'industrialAreas',
          title: 'itemList.industrialAreas.label',
          icon: 'sitemap',
          route: ['/industrial-area'],
        },
      ],
    },
    {
      id: 'backoffice',
      icon: '',
      label: 'categories.backoffice',
      collapsible: true,
      defaultExpanded: false,
      items: [
        {
          id: 'backofficeFactories',
          title: 'itemList.backofficeFactories.label',
          icon: 'warehouse',
          route: ['/backoffice/factories'],
        },
        {
          id: 'backofficeFollowUp',
          title: 'itemList.backofficeFollowUp.label',
          icon: 'list-check',
          route: ['/backoffice/follow-up'],
        },
        {
          id: 'backofficeFactoriesPending',
          title: 'itemList.backofficeFactoriesPending.label',
          icon: 'check-circle',
          route: ['/backoffice/factories/pending'],
        },
        {
          id: 'backofficeIndustrialAreasPending',
          title: 'itemList.backofficeIndustrialAreasPending.label',
          icon: 'check-circle',
          route: ['/backoffice/industrial-areas/pending'],
        },
        {
          id: 'backofficeMachines',
          title: 'itemList.backofficeMachines.label',
          icon: 'gears',
          route: ['/backoffice/machines'],
        },
        {
          id: 'backofficeMachinesPending',
          title: 'itemList.backofficeMachinesPending.label',
          icon: 'check-circle',
          route: ['/backoffice/machines/pending'],
        },
        {
          id: 'backofficeCapabilitySuggestions',
          title: 'itemList.backofficeCapabilitySuggestions.label',
          icon: 'lightbulb',
          route: ['/backoffice/capability-suggestions'],
        },
        {
          id: 'backofficeCountries',
          title: 'itemList.backofficeCountries.label',
          icon: 'globe',
          route: ['/backoffice/countries'],
        },
        {
          id: 'backofficeIndustrialTypes',
          title: 'itemList.backofficeIndustrialTypes.label',
          icon: 'sitemap',
          route: ['/backoffice/industrial-types'],
        },
        {
          id: 'backofficeTechnologies',
          title: 'itemList.backofficeTechnologies.label',
          icon: 'microchip',
          route: ['/backoffice/technologies'],
        },
        {
          id: 'backofficeContacts',
          title: 'itemList.backofficeContacts.label',
          icon: 'address-book',
          route: ['/backoffice/contacts'],
        },
        {
          id: 'backofficeChartGallery',
          title: 'itemList.backofficeChartGallery.label',
          icon: 'chart-scatter',
          route: ['/backoffice/chart-gallery'],
        },
      ],
    },
    RESOURCES_CATEGORY,
  ],
  buyer: [
    {
      id: 'features',
      icon: '',
      label: 'categories.features',
      items: [
        {
          id: 'dashboard',
          title: 'itemList.dashboard.label',
          icon: 'gauge',
          route: ['/dashboard'],
        },
        {
          id: 'map',
          title: 'itemList.map.label',
          icon: 'map',
          route: ['/map'],
        },
        {
          id: 'search',
          title: 'itemList.search.label',
          icon: 'search',
          route: ['/search'],
        },
        {
          id: 'projects',
          title: 'itemList.projects.label',
          icon: 'briefcase',
          route: ['/projects'],
        },
        REPORTING_MENU_ITEM,
      ],
    },
    {
      collapsible: true,
      defaultExpanded: false,
      id: 'organization',
      icon: '',
      label: 'categories.organization',
      items: [
        {
          id: 'company',
          title: 'itemList.company.label',
          icon: 'building',
          route: ['/company'],
        },
        {
          id: 'team',
          title: 'itemList.team.label',
          icon: 'users',
          route: ['/company', ':companyId', 'team'],
        },
        {
          id: 'factories',
          title: 'itemList.factories.label',
          icon: 'warehouse',
          route: ['/factories'],
        },
      ],
    },
    RESOURCES_CATEGORY,
  ],
  government: [
    {
      id: 'features',
      icon: '',
      label: 'categories.features',
      items: [
        {
          id: 'dashboard',
          title: 'itemList.dashboard.label',
          icon: 'gauge',
          route: ['/dashboard'],
        },
        {
          id: 'map',
          title: 'itemList.map.label',
          icon: 'map',
          route: ['/map'],
        },
        REPORTING_MENU_ITEM,
      ],
    },
    {
      collapsible: true,
      defaultExpanded: false,
      id: 'organization',
      icon: '',
      label: 'categories.organization',
      items: [
        {
          id: 'company',
          title: 'itemList.company.label',
          icon: 'building',
          route: ['/company'],
        },
        {
          id: 'team',
          title: 'itemList.team.label',
          icon: 'users',
          route: ['/company', ':companyId', 'team'],
        },
      ],
    },
    RESOURCES_CATEGORY,
  ],
  zone: [
    {
      id: 'features',
      icon: '',
      label: 'categories.features',
      items: [
        {
          id: 'dashboard',
          title: 'itemList.dashboard.label',
          icon: 'gauge',
          route: ['/dashboard'],
        },
        {
          id: 'map',
          title: 'itemList.map.label',
          icon: 'map',
          route: ['/map'],
        },
        REPORTING_MENU_ITEM,
      ],
    },
    {
      collapsible: true,
      defaultExpanded: false,
      id: 'organization',
      icon: '',
      label: 'categories.organization',
      items: [
        {
          id: 'company',
          title: 'itemList.company.label',
          icon: 'building',
          route: ['/company'],
        },
        {
          id: 'team',
          title: 'itemList.team.label',
          icon: 'users',
          route: ['/company', ':companyId', 'team'],
        },
        {
          id: 'industrialAreas',
          title: 'itemList.industrialAreas.label',
          icon: 'sitemap',
          route: ['/industrial-area'],
        },
      ],
    },
    RESOURCES_CATEGORY,
  ],
  supplier: [
    {
      id: 'features',
      icon: '',
      label: 'categories.features',
      items: [
        {
          id: 'dashboard',
          title: 'itemList.dashboard.label',
          icon: 'gauge',
          route: ['/dashboard'],
        },
        {
          id: 'map',
          title: 'itemList.map.label',
          icon: 'map',
          route: ['/map'],
        },
        {
          id: 'notifications',
          title: 'itemList.notifications.label',
          icon: 'bell',
          route: ['/notifications'],
        },
        {
          id: 'projects',
          title: 'itemList.projects.label',
          icon: 'briefcase',
          route: ['/projects'],
          disabled: true,
          tag: 'itemList.comingSoon',
        },
        REPORTING_MENU_ITEM,
      ],
    },
    {
      collapsible: true,
      defaultExpanded: false,
      id: 'organization',
      icon: '',
      label: 'categories.organization',
      items: [
        {
          id: 'company',
          title: 'itemList.company.label',
          icon: 'building',
          route: ['/company'],
        },
        {
          id: 'team',
          title: 'itemList.team.label',
          icon: 'users',
          route: ['/company', ':companyId', 'team'],
        },
        {
          id: 'factories',
          title: 'itemList.factories.label',
          icon: 'warehouse',
          route: ['/factories'],
        },
        {
          id: 'virtualTour',
          title: 'itemList.virtualTour.label',
          icon: 'eye',
          route: ['/virtual-tour'],
        },
      ],
    },
    RESOURCES_CATEGORY,
  ],
};
